import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import { type AssessmentSummary, assessmentsApi } from '../api/assessments.js';
import { type ClientDetail, clientsApi } from '../api/clients.js';
import { type Site, sitesApi } from '../api/sites.js';
import { CaptureTokensPanel } from '../components/CaptureTokensPanel.js';

export function ClientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSiteForm, setShowSiteForm] = useState(false);

  // Back button (BB-UX-01)
  useEffect(() => {
    setBackTo({ href: '/clients', label: 'Clientes' });
    return () => setBackTo(null);
  }, [setBackTo]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    Promise.all([clientsApi.get(id), sitesApi.listOfClient(id)])
      .then(([c, s]) => {
        if (cancelled) return;
        setClient(c);
        setSites(s.sites);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error cargando');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  function handleSiteCreated(s: Site): void {
    setSites((prev) => [...prev, s].sort((a, b) => a.name.localeCompare(b.name)));
    setShowSiteForm(false);
  }

  if (error) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
        <Link to="/clients" className="mt-3 inline-block text-sm text-brand-700">
          ← Volver
        </Link>
      </main>
    );
  }
  if (!client) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        <p className="text-sm text-slate-500">Cargando…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 space-y-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-slate-400">Cliente</p>
        <h1 className="text-2xl font-semibold text-brand-900">{client.name}</h1>
        <p className="text-sm text-slate-600">
          {client.sector ?? 'Sin sector'} {client.rnc ? `· RNC ${client.rnc}` : ''}
        </p>
      </header>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Sitios ({sites.length})</h2>
          <button
            type="button"
            onClick={() => setShowSiteForm((v) => !v)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            {showSiteForm ? 'Cancelar' : '+ Sitio'}
          </button>
        </div>
        {showSiteForm && id && <SiteForm clientId={id} onCreated={handleSiteCreated} />}
        {sites.length === 0 && !showSiteForm && (
          <p className="text-sm text-slate-500">Sin sitios registrados.</p>
        )}
        <ul className="space-y-2">
          {sites.map((s) => (
            <li
              key={s.id}
              className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between"
            >
              <div>
                <p className="font-medium text-slate-900">{s.name}</p>
                <p className="text-xs text-slate-500">
                  {s.address ?? 'Sin direccion'}
                  {s.criticality ? ` · ${s.criticality}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <Link
                  to={`/sites/${s.id}/assessments`}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Historico
                </Link>
                <button
                  type="button"
                  onClick={() => navigate(`/sites/${s.id}/assessments/new`)}
                  className="rounded-lg bg-brand-700 px-3 py-2 text-xs font-medium text-white hover:bg-brand-700/90"
                >
                  Nuevo assessment
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function SiteForm({
  clientId,
  onCreated,
}: {
  clientId: string;
  onCreated: (s: Site) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [criticality, setCriticality] = useState<'' | 'Z0' | 'Z1' | 'Z2' | 'Z3'>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const created = await sitesApi.create(clientId, {
        name: name.trim(),
        ...(address.trim() ? { address: address.trim() } : {}),
        ...(criticality ? { criticality } : {}),
      });
      onCreated(created);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void submit(e);
      }}
      className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3"
    >
      <input
        placeholder="Nombre del sitio"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
      <input
        placeholder="Direccion (opcional)"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
      <select
        value={criticality}
        onChange={(e) => setCriticality(e.target.value as '' | 'Z0' | 'Z1' | 'Z2' | 'Z3')}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      >
        <option value="">Criticidad (opcional)</option>
        <option value="Z0">Z0 — Publica</option>
        <option value="Z1">Z1 — General</option>
        <option value="Z2">Z2 — Restringida</option>
        <option value="Z3">Z3 — Critica</option>
      </select>
      {err && <p className="text-xs text-red-700">{err}</p>}
      <button
        type="submit"
        disabled={submitting || !name.trim()}
        className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
      >
        {submitting ? 'Creando...' : 'Crear sitio'}
      </button>
    </form>
  );
}

export function SiteAssessmentsPage(): JSX.Element {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();
  const [items, setItems] = useState<AssessmentSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [siteClientId, setSiteClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);

  // Back: vuelve al ClientDetailPage con el nombre del cliente como label (BB-UX-01).
  // El clientId se infiere del primer assessment o de la API de sitios.
  useEffect(() => {
    if (!siteId) return;
    sitesApi
      .get(siteId)
      .then((s) => {
        setSiteClientId(s.client_id);
        // Cargamos el nombre del cliente para el label del back
        clientsApi
          .get(s.client_id)
          .then((c) => {
            setClientName(c.name);
          })
          .catch(() => setClientName('Cliente'));
      })
      .catch(() => {
        /* silencioso */
      });
  }, [siteId]);

  useEffect(() => {
    if (!siteClientId) return;
    setBackTo({ href: `/clients/${siteClientId}`, label: clientName ?? 'Cliente' });
    return () => setBackTo(null);
  }, [setBackTo, siteClientId, clientName]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    assessmentsApi
      .listOfSite(siteId)
      .then((d) => {
        if (!cancelled) setItems(d.assessments);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Error');
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 space-y-5">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-brand-700 hover:underline"
      >
        ← Volver
      </button>
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-brand-900">Historico de assessments</h1>
        {siteId && (
          <button
            type="button"
            onClick={() => navigate(`/sites/${siteId}/assessments/new`)}
            className="rounded-lg bg-brand-700 px-3 py-2 text-xs font-medium text-white"
          >
            Nuevo
          </button>
        )}
      </header>
      {err && <p className="text-red-700 text-sm">{err}</p>}
      {items === null && !err && <p className="text-sm text-slate-500">Cargando…</p>}
      {items?.length === 0 && (
        <p className="text-sm text-slate-500">Sin assessments para este sitio.</p>
      )}

      {siteId && <CaptureTokensPanel siteId={siteId} />}
      <ul className="space-y-2">
        {items?.map((a) => (
          <li key={a.id}>
            <Link
              to={
                a.status === 'completed'
                  ? `/assessments/${a.id}/closed`
                  : `/assessments/${a.id}/capture`
              }
              className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-brand-500"
            >
              <p className="font-medium text-slate-900">
                {a.completed_at
                  ? `Cerrado ${new Date(a.completed_at).toLocaleString('es-DO')}`
                  : `Iniciado ${a.started_at ? new Date(a.started_at).toLocaleString('es-DO') : '—'}`}
              </p>
              <p className="text-xs text-slate-500">Estado: {a.status}</p>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
